import React from 'react';
import { Box, Checkbox } from '@material-ui/core';
import { TableGroupRow as DevExpressTableGroupRow } from '@devexpress/dx-react-grid-material-ui';
import {
  GroupRow,
  TableGroupRow as TableGroupRowBase,
} from '@devexpress/dx-react-grid';
import IndeterminateCheckBoxIcon from '@material-ui/icons/IndeterminateCheckBox';
import clsx from 'clsx';
import { Actions, Getters, TemplateConnector } from '@devexpress/dx-react-core';
import useIsSomeActionExecuting from '../../../../lowLevel/FormAction/useIsSomeActionExecuting';
import useTableContext from '../../useTableContext';
import useStyles from './TableGroupSelectCell.styles';
import { TableRow } from '../../../../../services/Main/types.Component';

interface Props extends TableGroupRowBase.CellProps {
  row: GroupRow & { compoundKey?: string | number };
  className?: string;
}

const TableGroupSelectCell: React.FC<Props> = ({
  row,
  tableRow,
  tableColumn,
  column,
  className,
  children,
  ...restProps
}) => {
  const classes = useStyles();
  const { isSomeActionExecuting } = useIsSomeActionExecuting();
  const { rowsFromState: originalRows, groupingConfig } = useTableContext();
  const isDisabled = isSomeActionExecuting;

  return (
    <TemplateConnector>
      {(
        { selection: selectedRowIds }: Getters,
        { toggleSelection }: Actions
      ) => {
        // 1. Сбор имен колонок группировки до текущей
        const order =
          groupingConfig?.defaultGrouping?.map((g: any) => g.columnName) || [];
        const idx = order.indexOf(column?.name || '');
        const groupingColumns = idx === -1 ? [] : order.slice(0, idx + 1);

        // 2. Получаем массив значений групп до текущего уровня
        let groupValues: (string | number)[] = [];

        if (row) {
          if (row.compoundKey && typeof row.compoundKey === 'string') {
            groupValues = row.compoundKey.split('|');
          } else {
            groupValues = [row.key];
          }
        }

        // 3. Фильтруем дочерние строки из исходных данных
        const childRows =
          !row || groupingColumns.length === 0
            ? []
            : originalRows.filter((r: TableRow) =>
                groupingColumns.every((col: string, index: number) => {
                  const expected = groupValues[index];
                  const cellValue = r[col];

                  return typeof cellValue === 'object' && cellValue?.label
                    ? cellValue.label === expected
                    : cellValue === expected;
                })
              );

        const childRowIds: (number | string)[] = childRows.map(
          (r: TableRow) => r.id
        );

        // 4. Вычисляем, все ли дочерние строки выбраны, или хотя бы одна
        const selectedSet = new Set(selectedRowIds);
        const allSelected =
          childRowIds.length > 0 &&
          childRowIds.every((id) => selectedSet.has(id));
        const someSelected = childRowIds.some((id) => selectedSet.has(id));

        // 5. Логика клика: инвертированная логика как в TableSelectAllCell
        const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation();
          e.preventDefault();
          if (childRowIds.length === 0) return;

          // Инвертируем логику: если что-то выбрано (allSelected или someSelected),
          // то снимаем выбор, иначе выбираем все
          if (allSelected || someSelected) {
            // Снимаем выбор со всех выбранных строк группы
            const selectedChildRowIds = childRowIds.filter((id) =>
              selectedSet.has(id)
            );
            if (selectedChildRowIds.length > 0) {
              toggleSelection({ rowIds: selectedChildRowIds });
            }
          } else {
            // Выбираем все невыбранные строки группы
            const unselectedChildRowIds = childRowIds.filter(
              (id) => !selectedSet.has(id)
            );
            if (unselectedChildRowIds.length > 0) {
              toggleSelection({ rowIds: unselectedChildRowIds });
            }
          }
        };

        return (
          <DevExpressTableGroupRow.Cell
            row={row}
            tableRow={tableRow}
            tableColumn={tableColumn}
            column={column}
            {...restProps}
            className={clsx(className, classes.groupCell)}
          >
            <Box component="span" display="inline-flex" alignItems="center">
              <Checkbox
                className={classes.checkbox}
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                indeterminateIcon={
                  <IndeterminateCheckBoxIcon
                    color={isDisabled ? 'disabled' : 'primary'}
                  />
                }
                color="primary"
                disabled={isDisabled || childRowIds.length === 0}
                onClick={handleClick}
                onMouseDown={(e) => e.stopPropagation()}
              />
              <Box className={classes.groupText}>
                <span className={classes.groupTitle}>
                  {`${column?.title}: `}
                </span>
                {children || row?.key}
              </Box>
            </Box>
          </DevExpressTableGroupRow.Cell>
        );
      }}
    </TemplateConnector>
  );
};

export default TableGroupSelectCell;
